import React, { useState } from "react";
import { AlertTriangle, FileDown, Loader2, Trash2, X, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";

/**
 * Two-step tenant deletion:
 *   1. User MUST download the full tenant archive PDF (statement + payment ledger)
 *   2. User MUST type the tenant's full name exactly
 *   3. Only then is the Delete button enabled
 *
 * On confirm: cascades payments, deposit ledger and any legacy contract for the tenant.
 */
export default function DeleteTenantModal({ tenant, onCancel, onConfirmDelete }) {
  const [downloaded, setDownloaded] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [typed, setTyped] = useState("");
  const [deleting, setDeleting] = useState(false);

  const nameOk = typed.trim() === (tenant.full_name || "").trim();
  const canDelete = downloaded && nameOk && !deleting;

  const download = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem("roost_token");
      const res = await fetch(`${API}/tenants/${tenant.id}/statement`, {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!res.ok) throw new Error("PDF error");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `tenant-archive-${(tenant.full_name || "tenant").replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setDownloaded(true);
      toast.success("Archive downloaded — you may now delete");
    } catch {
      toast.error("Could not generate archive");
    } finally {
      setDownloading(false);
    }
  };

  const confirm = async () => {
    setDeleting(true);
    try {
      await onConfirmDelete();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="delete-tenant-modal">
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-xl fade-up" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-red-50 text-red-700 grid place-items-center">
              <AlertTriangle className="w-4 h-4" />
            </div>
            <h3 className="font-display font-semibold text-lg">Delete tenant</h3>
          </div>
          <button onClick={onCancel} className="text-slate-400 hover:text-slate-900 transition" data-testid="delete-tenant-close-btn">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-6 space-y-5">
          <div className="text-sm text-slate-700">
            You are about to permanently delete <b>{tenant.full_name}</b> along with every payment record, deposit ledger entry and lease attached to this tenant. <b>This cannot be undone.</b>
          </div>

          {/* Step 1 */}
          <div className="border border-slate-200 rounded-lg p-4">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Step 1</div>
                <div className="font-display font-semibold">Download tenant archive</div>
                <p className="text-sm text-slate-600 mt-1">A PDF with the full payment ledger, lease terms and contact info for your records. Required before deletion.</p>
              </div>
              {downloaded ? (
                <span className="pill pill-paid" data-testid="tenant-archive-downloaded-pill">
                  <CheckCircle2 className="w-3 h-3" /> Saved
                </span>
              ) : (
                <button
                  onClick={download}
                  disabled={downloading}
                  data-testid="download-tenant-archive-btn"
                  className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg bg-slate-900 text-white text-xs font-medium hover:bg-slate-800 disabled:opacity-60"
                >
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />}
                  {downloading ? "Preparing…" : "Download PDF"}
                </button>
              )}
            </div>
          </div>

          {/* Step 2 */}
          <div className={`border rounded-lg p-4 ${downloaded ? "border-slate-200" : "border-slate-200 opacity-60"}`}>
            <div className="text-xs uppercase tracking-widest text-slate-500 mb-1">Step 2</div>
            <div className="font-display font-semibold">Type the tenant&apos;s full name to confirm</div>
            <p className="text-sm text-slate-600 mt-1 mb-3">
              Type <span className="font-mono-plex bg-slate-100 px-1.5 py-0.5 rounded">{tenant.full_name}</span> exactly.
            </p>
            <input
              type="text"
              value={typed}
              disabled={!downloaded}
              onChange={(e) => setTyped(e.target.value)}
              data-testid="delete-tenant-confirm-name-input"
              placeholder={tenant.full_name}
              className="w-full h-10 px-3 bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 text-sm disabled:bg-slate-50"
            />
          </div>
        </div>

        <div className="px-6 py-4 border-t border-slate-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            data-testid="delete-tenant-cancel-btn"
            className="h-10 px-4 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            onClick={confirm}
            disabled={!canDelete}
            data-testid="delete-tenant-confirm-btn"
            className="h-10 px-4 rounded-lg bg-red-600 text-white text-sm font-medium hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            Delete permanently
          </button>
        </div>
      </div>
    </div>
  );
}
